import {
  ASSIGNMENT_ROUTES,
  BUSINESS_CATALOG_ROUTES,
  BUSINESS_REQUEST_ROUTES,
  CUSTOMER_REQUEST_ROUTES,
  GHALISHOR_BUSINESS_ID_HEADER,
  GHALISHOR_IDEMPOTENCY_HEADER,
  FLEET_ROUTES,
  IDENTITY_ACCESS_ROUTES,
  PARTNERSHIP_ROUTES,
  PASSKEY_AUTH_ROUTES,
  PASSWORD_AUTH_ROUTES,
  PLATFORM_OPS_ROUTES,
  PUSH_ROUTES,
  MESSAGING_ROUTES,
  ORDER_ASSISTANT_ROUTES,
  PROFILE_ROUTES,
  isGhalishorIdentityAccessErrorCode,
  type ApplyWave2Request,
  type AuthenticationResponseJSON,
  type BusinessCatalogResponse,
  type BusinessContextResponse,
  type BusinessInboxItem,
  type BusinessLogisticsSettings,
  type BusinessReportsSummary,
  type BusinessTeamResponse,
  type CreateBusinessResponse,
  type CreateServiceRequestRequest,
  type CurrentUserResponse,
  type DeletePlatformEntityResponse,
  type IdentityAccessApiErrorBody,
  type ImpersonationIntent,
  type ImpersonationSessionResponse,
  type ListBusinessesResponse,
  type ListPartnershipInquiriesResponse,
  type ListPasskeysResponse,
  type ListPlatformBusinessesResponse,
  type ListPlatformRequestsResponse,
  type ListPlatformUsersResponse,
  type ListPlatformCustomersResponse,
  type PlatformCustomerDetail,
  type ListPlatformBusinessMembersResponse,
  type PlatformBusinessMemberDetail,
  type UpdatePlatformBusinessMemberRequest,
  type ListPlatformTeamResponse,
  type PlatformTeamMemberSummary,
  type PlatformAccountSecurityStatus,
  type PlatformAccountSupportActionResponse,
  type PlatformAccountSupportRequest,
  type PlatformResetAccountPasswordRequest,
  type CreatePlatformTeamMemberRequest,
  type UpdatePlatformTeamMemberRequest,
  type ListPlatformFleetDriversResponse,
  type ListBusinessDriversResponse,
  type ListBusinessDispatchRunsResponse,
  type ListDispatchCandidatesResponse,
  type DispatchRunSummary,
  type PlanDispatchRunRequest,
  type LogoutAllResponse,
  type LogoutResponse,
  type MatchingFlowType,
  type OfferSummary,
  type PartnershipInquirySummary,
  type PasskeyCredentialSummary,
  type PasskeyLoginOptionsResponse,
  type PasskeyLoginVerifyResponse,
  type PasskeyRegisterOptionsResponse,
  type PlatformDashboardResponse,
  type PlatformMeResponse,
  type PlatformRequestDetail,
  type PlatformBusinessSummary,
  type PlatformBusinessDetail,
  type PlatformUserSummary,
  type PasswordChangeResponse,
  type PasswordForgotResponse,
  type PasswordLoginResponse,
  type PasswordRegisterResponse,
  type PasswordResetResponse,
  type PasswordStatusResponse,
  type ProfileResponse,
  type ReverseGeocodeResponse,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicFirebaseConfigResponse,
  type RegisterPushTokenResponse,
  type PublicKeyCredentialRequestOptionsJSON,
  type RefreshSessionResponse,
  type RegistrationResponseJSON,
  type RequestOtpResponse,
  type ResolvedAuthPolicyResponse,
  type RecoveryStatusResponse,
  type GenerateRecoveryCodesResponse,
  type ServiceRequestSummary,
  type SendPushCampaignRequest,
  type SendPushCampaignResponse,
  type SendBusinessDirectMessageRequest,
  type ScheduleCustomerReminderRequest,
  type PlatformNotificationSummary,
  type ListPlatformNotificationsResponse,
  type ListBusinessDirectMessagesResponse,
  type SubmitPartnershipInquiryRequest,
  type SubmitPartnershipInquiryResponse,
  type SubmitServiceRequestResponse,
  type UpdateBusinessLogisticsRequest,
  type UpdatePartnershipInquiryRequest,
  type UpdatePasskeyRequest,
  type UpdatePlatformBusinessRequest,
  type UpdatePlatformUserRequest,
  type UpdateProfileRequest,
  type UpdateServiceRequestRequest,
  type UpsertBusinessCatalogRequest,
  type VerifyOtpResponse,
  type Wave2Bundle,
  type ListMessagingContactsResponse,
  type ListConversationsResponse,
  type ConversationDetail,
  type ConversationMessage,
  type CreateConversationRequest,
  type SendConversationMessageRequest,
  type UpdateConversationMessageRequest,
  type MessagingUpdatesResponse,
} from "@ghalishor/contracts";
import {
  VOICE_EXPERT_V3_ROUTE,
  type VoiceEventRequest,
  type VoiceEventResponse,
} from "@ghalishor/voice-core";
import { MemoryIdentitySessionStore } from "./session-store";

export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

export class IdentityApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "IdentityApiError";
  }
}

interface RequestOptions {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  readonly authenticated?: boolean;
  readonly headers?: Readonly<Record<string, string>>;
  readonly retryAfterRefresh?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.trim().length === 0) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: "پاسخ نامعتبر از سرور دریافت شد." };
  }
}

function toApiError(response: Response, body: unknown): IdentityApiError {
  const rawCode = isRecord(body) ? body.code : undefined;
  const rawMessage = isRecord(body) ? body.message : undefined;
  const code =
    typeof rawCode === "string" && isGhalishorIdentityAccessErrorCode(rawCode)
      ? rawCode
      : "GHALISHOR_INTERNAL_ERROR";
  const message =
    typeof rawMessage === "string" && rawMessage.trim().length > 0
      ? rawMessage
      : "درخواست انجام نشد. دوباره تلاش کنید.";

  return new IdentityApiError(response.status, code, message, body);
}

export class IdentityApiClient {
  private refreshInFlight: Promise<RefreshSessionResponse> | null = null;

  constructor(
    private readonly fetcher: FetchLike,
    private readonly session: MemoryIdentitySessionStore,
    private readonly baseUrl = "",
  ) {}

  async requestOtp(mobile: string): Promise<RequestOtpResponse> {
    return this.request<RequestOtpResponse>(IDENTITY_ACCESS_ROUTES.otpRequest, {
      method: "POST",
      body: { mobile, autofill: "web" },
    });
  }

  async verifyOtp(input: {
    readonly challengeId: string;
    readonly mobile: string;
    readonly code: string;
  }): Promise<VerifyOtpResponse> {
    const response = await this.request<VerifyOtpResponse>(
      IDENTITY_ACCESS_ROUTES.otpVerify,
      { method: "POST", body: input },
    );
    this.session.setAccessGrant(response);
    this.session.setIdentity(response.user, response.memberships);
    return response;
  }

  async startImpersonation(input: {
    targetUserId: string;
    intent: ImpersonationIntent;
    businessId?: string;
  }): Promise<ImpersonationSessionResponse> {
    const response = await this.request<ImpersonationSessionResponse>(
      IDENTITY_ACCESS_ROUTES.impersonationStart,
      { method: "POST", authenticated: true, body: input },
    );
    this.session.setAccessGrant(response);
    this.session.setIdentity(
      response.user,
      response.memberships,
      response.impersonation,
    );
    return response;
  }

  async stopImpersonation(): Promise<ImpersonationSessionResponse> {
    const response = await this.request<ImpersonationSessionResponse>(
      IDENTITY_ACCESS_ROUTES.impersonationStop,
      { method: "POST", authenticated: true, body: {} },
    );
    this.session.setAccessGrant(response);
    this.session.setIdentity(response.user, response.memberships, null);
    this.session.clearSelectedBusinessId();
    return response;
  }

  async refreshSession(): Promise<RefreshSessionResponse> {
    if (this.refreshInFlight !== null) return this.refreshInFlight;

    const operation = this.request<RefreshSessionResponse>(
      IDENTITY_ACCESS_ROUTES.refresh,
      { method: "POST", retryAfterRefresh: false },
    )
      .then((response) => {
        this.session.setAccessGrant(response);
        return response;
      })
      .catch((error: unknown) => {
        this.session.clearAuthentication();
        throw error;
      })
      .finally(() => {
        this.refreshInFlight = null;
      });

    this.refreshInFlight = operation;
    return operation;
  }

  async currentUser(): Promise<CurrentUserResponse> {
    const response = await this.request<CurrentUserResponse>(
      IDENTITY_ACCESS_ROUTES.me,
      {
        authenticated: true,
      },
    );
    this.session.setIdentity(
      response.user,
      response.memberships,
      response.impersonation,
    );
    return response;
  }

  async listBusinesses(): Promise<ListBusinessesResponse> {
    const response = await this.request<ListBusinessesResponse>(
      IDENTITY_ACCESS_ROUTES.businesses,
      { authenticated: true },
    );
    this.session.replaceMemberships(response.memberships);
    return response;
  }

  async createBusiness(
    name: string,
    idempotencyKey: string,
  ): Promise<CreateBusinessResponse> {
    const response = await this.request<CreateBusinessResponse>(
      IDENTITY_ACCESS_ROUTES.businesses,
      {
        method: "POST",
        authenticated: true,
        body: { name },
        headers: { [GHALISHOR_IDEMPOTENCY_HEADER]: idempotencyKey },
      },
    );
    this.session.replaceMemberships([
      ...this.session.snapshot().memberships,
      response.membership,
    ]);
    this.session.setSelectedBusinessId(response.business.id);
    return response;
  }

  async businessContext(businessId: string): Promise<BusinessContextResponse> {
    return this.request<BusinessContextResponse>(
      IDENTITY_ACCESS_ROUTES.businessContext(businessId),
      {
        authenticated: true,
        headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      },
    );
  }

  async logout(): Promise<LogoutResponse> {
    try {
      return await this.request<LogoutResponse>(IDENTITY_ACCESS_ROUTES.logout, {
        method: "POST",
        retryAfterRefresh: false,
      });
    } finally {
      this.session.clearAuthentication();
    }
  }

  async logoutAll(): Promise<LogoutAllResponse> {
    try {
      return await this.request<LogoutAllResponse>(
        IDENTITY_ACCESS_ROUTES.logoutAll,
        {
          method: "POST",
          authenticated: true,
        },
      );
    } finally {
      this.session.clearAuthentication({ preserveBusinessSelection: false });
    }
  }

  async getProfile(): Promise<ProfileResponse> {
    return this.request<ProfileResponse>(PROFILE_ROUTES.profile, {
      authenticated: true,
    });
  }

  async updateProfile(input: UpdateProfileRequest): Promise<ProfileResponse> {
    return this.request<ProfileResponse>(PROFILE_ROUTES.profile, {
      method: "PUT",
      authenticated: true,
      body: input,
    });
  }

  async reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeResponse> {
    return this.request<ReverseGeocodeResponse>(PROFILE_ROUTES.reverseGeocode(lat, lng), {
      authenticated: true,
    });
  }

  async listRequests(): Promise<readonly ServiceRequestSummary[]> {
    return this.request<readonly ServiceRequestSummary[]>(
      CUSTOMER_REQUEST_ROUTES.list,
      {
        authenticated: true,
      },
    );
  }

  async sendVoiceEvent(
    input: VoiceEventRequest,
    authenticated: boolean,
  ): Promise<VoiceEventResponse> {
    return this.request<VoiceEventResponse>(VOICE_EXPERT_V3_ROUTE, {
      method: "POST",
      authenticated,
      body: input,
    });
  }

  async transcribeOrderAudio(audioBase64: string, audioMimeType: "audio/webm" | "audio/mp4" | "audio/mpeg" | "audio/wav" | "audio/ogg" | "audio/x-m4a"): Promise<string> {
    const result = await this.request<{ transcript: string }>(ORDER_ASSISTANT_ROUTES.transcribe, {
      method: "POST", authenticated: true, body: { audioBase64, audioMimeType },
    });
    return result.transcript;
  }

  async createRequest(
    input: CreateServiceRequestRequest,
  ): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(CUSTOMER_REQUEST_ROUTES.create, {
      method: "POST",
      authenticated: true,
      body: input,
    });
  }

  async getRequest(id: string): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(
      CUSTOMER_REQUEST_ROUTES.byId(id),
      {
        authenticated: true,
      },
    );
  }

  async updateRequest(
    id: string,
    input: UpdateServiceRequestRequest,
  ): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(
      CUSTOMER_REQUEST_ROUTES.byId(id),
      {
        method: "PUT",
        authenticated: true,
        body: input,
      },
    );
  }

  async submitRequest(
    id: string,
    flowType: MatchingFlowType = "auto_broadcast",
  ): Promise<SubmitServiceRequestResponse> {
    return this.request<SubmitServiceRequestResponse>(
      CUSTOMER_REQUEST_ROUTES.submit(id),
      {
        method: "POST",
        authenticated: true,
        body: { flowType },
      },
    );
  }

  async cancelRequest(id: string): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(
      CUSTOMER_REQUEST_ROUTES.cancel(id),
      {
        method: "POST",
        authenticated: true,
      },
    );
  }

  async listRequestOffers(id: string): Promise<readonly OfferSummary[]> {
    return this.request<readonly OfferSummary[]>(
      CUSTOMER_REQUEST_ROUTES.offers(id),
      {
        authenticated: true,
      },
    );
  }

  async selectOffer(
    requestId: string,
    offerId: string,
  ): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(
      CUSTOMER_REQUEST_ROUTES.selectOffer(requestId, offerId),
      { method: "POST", authenticated: true },
    );
  }

  async applyWave2(
    requestId: string,
    bundle: Wave2Bundle,
  ): Promise<ServiceRequestSummary> {
    const body: ApplyWave2Request = { bundle };
    return this.request<ServiceRequestSummary>(
      CUSTOMER_REQUEST_ROUTES.wave2(requestId),
      {
        method: "POST",
        authenticated: true,
        body,
      },
    );
  }

  async businessInbox(
    businessId: string,
  ): Promise<readonly BusinessInboxItem[]> {
    return this.request<readonly BusinessInboxItem[]>(
      BUSINESS_REQUEST_ROUTES.inbox,
      {
        authenticated: true,
        headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      },
    );
  }

  async acceptOffer(
    businessId: string,
    offerId: string,
  ): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(
      BUSINESS_REQUEST_ROUTES.acceptOffer(offerId),
      {
        method: "POST",
        authenticated: true,
        headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      },
    );
  }

  async rejectOffer(
    businessId: string,
    offerId: string,
  ): Promise<ServiceRequestSummary> {
    return this.request<ServiceRequestSummary>(
      BUSINESS_REQUEST_ROUTES.rejectOffer(offerId),
      {
        method: "POST",
        authenticated: true,
        headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      },
    );
  }

  async customerAssignment(
    id: string,
  ): Promise<import("@ghalishor/contracts").AssignmentSummary> {
    return this.request(ASSIGNMENT_ROUTES.customerAssignment(id), {
      authenticated: true,
    });
  }

  async businessAssignment(
    businessId: string,
    id: string,
  ): Promise<import("@ghalishor/contracts").AssignmentSummary> {
    return this.request(ASSIGNMENT_ROUTES.businessAssignment(id), {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async scheduleCustomer(
    id: string,
    body: import("@ghalishor/contracts").ScheduleRequestBody,
  ): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.customerScheduleCounter(id), {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async confirmScheduleCustomer(id: string): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.customerScheduleConfirm(id), {
      method: "POST",
      authenticated: true,
    });
  }

  async scheduleBusiness(
    businessId: string,
    id: string,
    body: import("@ghalishor/contracts").ScheduleRequestBody,
  ): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.businessSchedule(id), {
      method: "POST",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async confirmScheduleBusiness(
    businessId: string,
    id: string,
  ): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.businessScheduleConfirm(id), {
      method: "POST",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async proposeConfirmedPrice(
    businessId: string,
    id: string,
    body: import("@ghalishor/contracts").ProposeConfirmedPriceRequest,
  ): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.businessProposePrice(id), {
      method: "POST",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async confirmPrice(id: string): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.customerConfirmPrice(id), {
      method: "POST",
      authenticated: true,
    });
  }

  async rejectPrice(id: string): Promise<ServiceRequestSummary> {
    return this.request(ASSIGNMENT_ROUTES.customerRejectPrice(id), {
      method: "POST",
      authenticated: true,
    });
  }

  async businessActive(
    businessId: string,
  ): Promise<readonly import("@ghalishor/contracts").ActiveBusinessJob[]> {
    return this.request(ASSIGNMENT_ROUTES.businessActive, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async submitReview(
    id: string,
    body: import("@ghalishor/contracts").CreateReviewRequest,
  ): Promise<{ readonly success: true }> {
    return this.request(ASSIGNMENT_ROUTES.customerReview(id), {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async getBusinessProfile(
    businessId: string,
  ): Promise<import("@ghalishor/contracts").BusinessProfileResponse> {
    return this.request(ASSIGNMENT_ROUTES.businessProfile, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async updateBusinessProfile(
    businessId: string,
    body: import("@ghalishor/contracts").UpdateBusinessProfileRequest,
  ): Promise<import("@ghalishor/contracts").BusinessProfileResponse> {
    return this.request(ASSIGNMENT_ROUTES.businessProfile, {
      method: "PUT",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async listRateCards(
    businessId: string,
  ): Promise<readonly import("@ghalishor/contracts").BusinessRateCard[]> {
    return this.request(ASSIGNMENT_ROUTES.businessRateCards, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async upsertRateCards(
    businessId: string,
    body: import("@ghalishor/contracts").UpsertBusinessRateCardsRequest,
  ): Promise<readonly import("@ghalishor/contracts").BusinessRateCard[]> {
    return this.request(ASSIGNMENT_ROUTES.businessRateCards, {
      method: "PUT",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async getBusinessCatalog(
    businessId: string,
  ): Promise<BusinessCatalogResponse> {
    return this.request(BUSINESS_CATALOG_ROUTES.catalog, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async upsertBusinessCatalog(
    businessId: string,
    body: UpsertBusinessCatalogRequest,
  ): Promise<BusinessCatalogResponse> {
    return this.request(BUSINESS_CATALOG_ROUTES.catalog, {
      method: "PUT",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async getBusinessLogistics(
    businessId: string,
  ): Promise<BusinessLogisticsSettings> {
    return this.request(BUSINESS_CATALOG_ROUTES.logistics, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async updateBusinessLogistics(
    businessId: string,
    body: UpdateBusinessLogisticsRequest,
  ): Promise<BusinessLogisticsSettings> {
    return this.request(BUSINESS_CATALOG_ROUTES.logistics, {
      method: "PUT",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async getBusinessReportsSummary(
    businessId: string,
  ): Promise<BusinessReportsSummary> {
    return this.request(BUSINESS_CATALOG_ROUTES.reportsSummary, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async getBusinessTeam(businessId: string): Promise<BusinessTeamResponse> {
    return this.request(BUSINESS_CATALOG_ROUTES.team, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async inviteBusinessTeamMember(
    businessId: string,
    body: import("@ghalishor/contracts").InviteBusinessTeamMemberRequest,
  ): Promise<import("@ghalishor/contracts").BusinessTeamMember> {
    return this.request(BUSINESS_CATALOG_ROUTES.teamInvite, {
      method: "POST",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async resetBusinessTeamMemberPassword(
    businessId: string,
    membershipId: string,
    temporaryPassword: string,
  ): Promise<{ success: true }> {
    return this.request(
      BUSINESS_CATALOG_ROUTES.teamResetPassword(membershipId),
      {
        method: "POST",
        authenticated: true,
        headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
        body: { temporaryPassword },
      },
    );
  }

  async updateBusinessTeamMember(
    businessId: string,
    membershipId: string,
    body: import("@ghalishor/contracts").UpdateBusinessTeamMemberRequest,
  ): Promise<import("@ghalishor/contracts").BusinessTeamMember> {
    return this.request(
      BUSINESS_CATALOG_ROUTES.teamUpdateMember(membershipId),
      {
        method: "PATCH",
        authenticated: true,
        headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
        body,
      },
    );
  }

  async getBusinessDrivers(
    businessId: string,
  ): Promise<ListBusinessDriversResponse> {
    return this.request(FLEET_ROUTES.businessDrivers, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async getDispatchCandidates(
    businessId: string,
  ): Promise<ListDispatchCandidatesResponse> {
    return this.request(FLEET_ROUTES.dispatchCandidates, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async getBusinessDispatchRuns(
    businessId: string,
  ): Promise<ListBusinessDispatchRunsResponse> {
    return this.request(FLEET_ROUTES.dispatchRuns, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async planBusinessDispatchRun(
    businessId: string,
    body: PlanDispatchRunRequest,
  ): Promise<DispatchRunSummary> {
    return this.request(FLEET_ROUTES.planRun, {
      method: "POST",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body,
    });
  }

  async assignBusinessDispatchRun(
    businessId: string,
    runId: string,
    driverMembershipId: string,
  ): Promise<DispatchRunSummary> {
    return this.request(FLEET_ROUTES.assignRun(runId), {
      method: "PATCH",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body: { driverMembershipId },
    });
  }

  async cancelBusinessDispatchRun(
    businessId: string,
    runId: string,
    reason: string,
  ): Promise<DispatchRunSummary> {
    return this.request(FLEET_ROUTES.cancelRun(runId), {
      method: "PATCH",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body: { reason },
    });
  }

  async passwordLogin(
    mobile: string,
    password: string,
  ): Promise<PasswordLoginResponse> {
    const response = await this.request<PasswordLoginResponse>(
      PASSWORD_AUTH_ROUTES.login,
      {
        method: "POST",
        body: { mobile, password },
      },
    );
    this.session.setAccessGrant(response);
    this.session.setIdentity(response.user, response.memberships);
    return response;
  }

  async passwordRegister(input: {
    mobile: string;
    email: string;
    displayName: string;
    password: string;
  }): Promise<PasswordRegisterResponse> {
    const response = await this.request<PasswordRegisterResponse>(
      PASSWORD_AUTH_ROUTES.register,
      {
        method: "POST",
        body: input,
      },
    );
    this.session.setAccessGrant(response);
    this.session.setIdentity(response.user, response.memberships);
    return response;
  }

  async forgotPassword(email: string): Promise<PasswordForgotResponse> {
    return this.request<PasswordForgotResponse>(PASSWORD_AUTH_ROUTES.forgot, {
      method: "POST",
      body: { email },
    });
  }

  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<PasswordResetResponse> {
    return this.request<PasswordResetResponse>(PASSWORD_AUTH_ROUTES.reset, {
      method: "POST",
      body: { token, newPassword },
    });
  }

  async passwordStatus(): Promise<PasswordStatusResponse> {
    return this.request(PASSWORD_AUTH_ROUTES.status, {
      authenticated: true,
    });
  }

  async changePassword(body: {
    currentPassword?: string;
    newPassword: string;
  }): Promise<PasswordChangeResponse> {
    return this.request(PASSWORD_AUTH_ROUTES.change, {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async submitPartnershipInquiry(
    body: SubmitPartnershipInquiryRequest,
  ): Promise<SubmitPartnershipInquiryResponse> {
    return this.request(PARTNERSHIP_ROUTES.submit, {
      method: "POST",
      body,
    });
  }

  async listPartnershipInquiries(): Promise<ListPartnershipInquiriesResponse> {
    return this.request(PARTNERSHIP_ROUTES.list, { authenticated: true });
  }

  async updatePartnershipInquiry(
    id: string,
    body: UpdatePartnershipInquiryRequest,
  ): Promise<PartnershipInquirySummary> {
    return this.request(PARTNERSHIP_ROUTES.update(id), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async platformMe(): Promise<PlatformMeResponse> {
    return this.request(PLATFORM_OPS_ROUTES.me, { authenticated: true });
  }

  async platformDashboard(): Promise<PlatformDashboardResponse> {
    return this.request(PLATFORM_OPS_ROUTES.dashboard, { authenticated: true });
  }

  async listPlatformBusinesses(params?: {
    q?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListPlatformBusinessesResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.status) query.set("status", params.status);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      query.set("offset", String(params.offset));
    const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
    return this.request(`${PLATFORM_OPS_ROUTES.businesses}${suffix}`, {
      authenticated: true,
    });
  }

  async updatePlatformBusiness(
    id: string,
    body: UpdatePlatformBusinessRequest,
  ): Promise<PlatformBusinessSummary> {
    return this.request(PLATFORM_OPS_ROUTES.business(id), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async getPlatformBusiness(id: string): Promise<PlatformBusinessDetail> {
    return this.request(PLATFORM_OPS_ROUTES.business(id), {
      authenticated: true,
    });
  }

  async deletePlatformBusiness(
    id: string,
  ): Promise<DeletePlatformEntityResponse> {
    return this.request(PLATFORM_OPS_ROUTES.business(id), {
      method: "DELETE",
      authenticated: true,
    });
  }

  async listPlatformUsers(params?: {
    q?: string;
    platformRole?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListPlatformUsersResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.platformRole) query.set("platformRole", params.platformRole);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      query.set("offset", String(params.offset));
    const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
    return this.request(`${PLATFORM_OPS_ROUTES.users}${suffix}`, {
      authenticated: true,
    });
  }

  async updatePlatformUser(
    id: string,
    body: UpdatePlatformUserRequest,
  ): Promise<PlatformUserSummary> {
    return this.request(PLATFORM_OPS_ROUTES.user(id), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async deletePlatformUser(id: string): Promise<DeletePlatformEntityResponse> {
    return this.request(PLATFORM_OPS_ROUTES.user(id), {
      method: "DELETE",
      authenticated: true,
    });
  }

  async listPlatformCustomers(params?: {
    q?: string;
    city?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListPlatformCustomersResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.city) query.set("city", params.city);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      query.set("offset", String(params.offset));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.request(`${PLATFORM_OPS_ROUTES.customers}${suffix}`, {
      authenticated: true,
    });
  }

  async getPlatformCustomer(id: string): Promise<PlatformCustomerDetail> {
    return this.request(PLATFORM_OPS_ROUTES.customer(id), {
      authenticated: true,
    });
  }

  async listPlatformBusinessMembers(params?: {
    q?: string;
    businessId?: string;
    roleKey?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListPlatformBusinessMembersResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.businessId) query.set("businessId", params.businessId);
    if (params?.roleKey) query.set("roleKey", params.roleKey);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      query.set("offset", String(params.offset));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.request(`${PLATFORM_OPS_ROUTES.businessMembers}${suffix}`, {
      authenticated: true,
    });
  }

  async getPlatformBusinessMember(
    membershipId: string,
  ): Promise<PlatformBusinessMemberDetail> {
    return this.request(PLATFORM_OPS_ROUTES.businessMember(membershipId), {
      authenticated: true,
    });
  }

  async updatePlatformBusinessMember(
    membershipId: string,
    body: UpdatePlatformBusinessMemberRequest,
  ): Promise<PlatformBusinessMemberDetail> {
    return this.request(PLATFORM_OPS_ROUTES.businessMember(membershipId), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async listPlatformTeam(): Promise<ListPlatformTeamResponse> {
    return this.request(PLATFORM_OPS_ROUTES.platformTeam, {
      authenticated: true,
    });
  }

  async createPlatformTeamMember(
    body: CreatePlatformTeamMemberRequest,
  ): Promise<PlatformTeamMemberSummary> {
    return this.request(PLATFORM_OPS_ROUTES.platformTeam, {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async updatePlatformTeamMember(
    id: string,
    body: UpdatePlatformTeamMemberRequest,
  ): Promise<PlatformTeamMemberSummary> {
    return this.request(PLATFORM_OPS_ROUTES.platformTeamMember(id), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async getPlatformAccountSecurity(
    id: string,
  ): Promise<PlatformAccountSecurityStatus> {
    return this.request(PLATFORM_OPS_ROUTES.accountSecurity(id), {
      authenticated: true,
    });
  }

  async resetPlatformAccountPassword(
    id: string,
    body: PlatformResetAccountPasswordRequest,
  ): Promise<PlatformAccountSupportActionResponse> {
    return this.request(PLATFORM_OPS_ROUTES.resetAccountPassword(id), {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async revokePlatformAccountSessions(
    id: string,
    body: PlatformAccountSupportRequest,
  ): Promise<PlatformAccountSupportActionResponse> {
    return this.request(PLATFORM_OPS_ROUTES.revokeAccountSessions(id), {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async unlockPlatformAccount(
    id: string,
    body: PlatformAccountSupportRequest,
  ): Promise<PlatformAccountSupportActionResponse> {
    return this.request(PLATFORM_OPS_ROUTES.unlockAccount(id), {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async listPlatformFleetDrivers(): Promise<ListPlatformFleetDriversResponse> {
    return this.request(PLATFORM_OPS_ROUTES.fleetDrivers, {
      authenticated: true,
    });
  }

  async listPlatformRequests(params?: {
    q?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListPlatformRequestsResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.status) query.set("status", params.status);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      query.set("offset", String(params.offset));
    const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
    return this.request(`${PLATFORM_OPS_ROUTES.requests}${suffix}`, {
      authenticated: true,
    });
  }

  async getPlatformRequest(id: string): Promise<PlatformRequestDetail> {
    return this.request(PLATFORM_OPS_ROUTES.request(id), {
      authenticated: true,
    });
  }

  async deletePlatformRequest(
    id: string,
  ): Promise<DeletePlatformEntityResponse> {
    return this.request(PLATFORM_OPS_ROUTES.request(id), {
      method: "DELETE",
      authenticated: true,
    });
  }

  async rematchPlatformRequest(
    id: string,
  ): Promise<{ requestId: string; rematched: boolean; status: string }> {
    return this.request(PLATFORM_OPS_ROUTES.rematchRequest(id), {
      method: "POST",
      authenticated: true,
      body: {},
    });
  }

  async notePlatformRequest(
    id: string,
    note: string,
  ): Promise<PlatformRequestDetail> {
    return this.request(PLATFORM_OPS_ROUTES.noteRequest(id), {
      method: "POST",
      authenticated: true,
      body: { note },
    });
  }

  async retryPlatformRequestNotification(
    id: string,
  ): Promise<import("@ghalishor/contracts").PlatformRetryNotificationResponse> {
    return this.request(PLATFORM_OPS_ROUTES.retryRequestNotification(id), {
      method: "POST",
      authenticated: true,
      body: {},
    });
  }

  async listPlatformAudit(limit = 50): Promise<{
    items: readonly import("@ghalishor/contracts").PlatformAuditEntry[];
  }> {
    const query = new URLSearchParams({ limit: String(limit) });
    return this.request(`${PLATFORM_OPS_ROUTES.audit}?${query.toString()}`, {
      authenticated: true,
    });
  }

  async publicFirebaseConfig(): Promise<PublicFirebaseConfigResponse> {
    return this.request(PUSH_ROUTES.publicFirebaseConfig);
  }

  async registerPushToken(input: {
    token: string;
    platform: "android" | "ios" | "web";
    clientKind: "customer" | "business" | "platform";
    businessId?: string | null;
  }): Promise<RegisterPushTokenResponse> {
    return this.request(PUSH_ROUTES.registerDevice, {
      method: "POST",
      authenticated: true,
      body: input,
    });
  }

  async sendPushCampaign(
    body: SendPushCampaignRequest,
  ): Promise<SendPushCampaignResponse> {
    return this.request(PUSH_ROUTES.platformCampaigns, {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async sendBusinessDirectMessage(
    body: SendBusinessDirectMessageRequest,
  ): Promise<PlatformNotificationSummary> {
    return this.request(PUSH_ROUTES.platformBusinessMessages, {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async scheduleCustomerReminder(
    body: ScheduleCustomerReminderRequest,
  ): Promise<PlatformNotificationSummary> {
    return this.request(PUSH_ROUTES.platformCustomerReminders, {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async listPlatformNotifications(): Promise<ListPlatformNotificationsResponse> {
    return this.request(PUSH_ROUTES.platformNotificationHistory, {
      authenticated: true,
    });
  }

  async cancelPlatformNotification(
    id: string,
  ): Promise<PlatformNotificationSummary> {
    return this.request(PUSH_ROUTES.cancelPlatformNotification(id), {
      method: "POST",
      authenticated: true,
      body: {},
    });
  }

  async listBusinessDirectMessages(
    businessId: string,
  ): Promise<ListBusinessDirectMessagesResponse> {
    return this.request(PUSH_ROUTES.businessMessages, {
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
    });
  }

  async readBusinessDirectMessage(
    businessId: string,
    id: string,
  ): Promise<{ success: true }> {
    return this.request(PUSH_ROUTES.readBusinessMessage(id), {
      method: "POST",
      authenticated: true,
      headers: { [GHALISHOR_BUSINESS_ID_HEADER]: businessId },
      body: {},
    });
  }

  async messagingContacts(
    input: {
      q?: string;
      businessId?: string | null;
    } = {},
  ): Promise<ListMessagingContactsResponse> {
    const params = new URLSearchParams();
    if (input.q?.trim()) params.set("q", input.q.trim());
    if (input.businessId) params.set("businessId", input.businessId);
    const suffix = params.size ? `?${params.toString()}` : "";
    return this.request(`${MESSAGING_ROUTES.contacts}${suffix}`, {
      authenticated: true,
    });
  }

  async listConversations(): Promise<ListConversationsResponse> {
    return this.request(MESSAGING_ROUTES.conversations, {
      authenticated: true,
    });
  }

  async conversation(id: string): Promise<ConversationDetail> {
    return this.request(MESSAGING_ROUTES.conversation(id), {
      authenticated: true,
    });
  }

  async createConversation(
    body: CreateConversationRequest,
    businessId?: string | null,
  ): Promise<ConversationDetail> {
    return this.request(MESSAGING_ROUTES.conversations, {
      method: "POST",
      authenticated: true,
      headers: businessId
        ? { [GHALISHOR_BUSINESS_ID_HEADER]: businessId }
        : undefined,
      body,
    });
  }

  async sendConversationMessage(
    id: string,
    body: SendConversationMessageRequest,
  ): Promise<ConversationMessage> {
    return this.request(MESSAGING_ROUTES.messages(id), {
      method: "POST",
      authenticated: true,
      body,
    });
  }

  async updateConversationMessage(
    conversationId: string,
    messageId: string,
    body: UpdateConversationMessageRequest,
  ): Promise<ConversationMessage> {
    return this.request(MESSAGING_ROUTES.message(conversationId, messageId), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async deleteConversationMessage(
    conversationId: string,
    messageId: string,
  ): Promise<{ success: true }> {
    return this.request(MESSAGING_ROUTES.message(conversationId, messageId), {
      method: "DELETE",
      authenticated: true,
    });
  }

  async markConversationRead(
    id: string,
  ): Promise<{ success: true; readAt: string }> {
    return this.request(MESSAGING_ROUTES.read(id), {
      method: "POST",
      authenticated: true,
      body: {},
    });
  }

  async setConversationTyping(
    id: string,
    active: boolean,
  ): Promise<{ success: true }> {
    return this.request(MESSAGING_ROUTES.typing(id), {
      method: "POST",
      authenticated: true,
      body: { active },
    });
  }

  async messagingUpdates(after?: string): Promise<MessagingUpdatesResponse> {
    const suffix = after ? `?after=${encodeURIComponent(after)}` : "";
    return this.request(`${MESSAGING_ROUTES.updates}${suffix}`, {
      authenticated: true,
    });
  }

  async messagingAttachment(id: string): Promise<Blob> {
    return this.requestBlob(MESSAGING_ROUTES.attachment(id));
  }

  async getAuthPolicy(): Promise<ResolvedAuthPolicyResponse> {
    return this.request(PASSKEY_AUTH_ROUTES.policy, { authenticated: true });
  }

  async listPasskeys(): Promise<ListPasskeysResponse> {
    return this.request(PASSKEY_AUTH_ROUTES.list, { authenticated: true });
  }

  async passkeyRegisterOptions(): Promise<PasskeyRegisterOptionsResponse> {
    return this.request(PASSKEY_AUTH_ROUTES.registerOptions, {
      method: "POST",
      authenticated: true,
      body: {},
    });
  }

  async passkeyRegisterVerify(
    response: RegistrationResponseJSON,
    deviceName?: string,
  ): Promise<{ credential: PasskeyCredentialSummary }> {
    return this.request(PASSKEY_AUTH_ROUTES.registerVerify, {
      method: "POST",
      authenticated: true,
      body: { response, deviceName },
    });
  }

  async passkeyLoginOptions(
    mobile: string,
  ): Promise<PasskeyLoginOptionsResponse> {
    return this.request(PASSKEY_AUTH_ROUTES.loginOptions, {
      method: "POST",
      body: { mobile },
    });
  }

  async passkeyLoginVerify(
    mobile: string,
    response: AuthenticationResponseJSON,
  ): Promise<VerifyOtpResponse & { refreshToken?: string }> {
    return this.request(PASSKEY_AUTH_ROUTES.loginVerify, {
      method: "POST",
      body: { mobile, response },
    });
  }

  async updatePasskey(
    id: string,
    body: UpdatePasskeyRequest,
  ): Promise<PasskeyCredentialSummary> {
    return this.request(PASSKEY_AUTH_ROUTES.update(id), {
      method: "PATCH",
      authenticated: true,
      body,
    });
  }

  async revokePasskey(id: string): Promise<PasskeyCredentialSummary> {
    return this.request(PASSKEY_AUTH_ROUTES.revoke(id), {
      method: "DELETE",
      authenticated: true,
    });
  }

  async recoveryStatus(): Promise<RecoveryStatusResponse> {
    return this.request(PASSKEY_AUTH_ROUTES.recoveryStatus, {
      authenticated: true,
    });
  }

  async generateRecoveryCodes(): Promise<GenerateRecoveryCodesResponse> {
    return this.request(PASSKEY_AUTH_ROUTES.recoveryGenerate, {
      method: "POST",
      authenticated: true,
      body: {},
    });
  }

  async recoveryVerify(
    mobile: string,
    code: string,
  ): Promise<VerifyOtpResponse & { refreshToken?: string }> {
    return this.request(PASSKEY_AUTH_ROUTES.recoveryVerify, {
      method: "POST",
      body: { mobile, code },
    });
  }

  private async request<T>(
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const authenticated = options.authenticated === true;
    const headers = new Headers({
      Accept: "application/json",
      ...options.headers,
    });

    if (options.body !== undefined)
      headers.set("Content-Type", "application/json");

    if (authenticated) {
      const accessToken = this.session.getAccessToken();
      if (accessToken === null) {
        throw new IdentityApiError(
          401,
          "GHALISHOR_AUTH_REQUIRED",
          "ورود به حساب لازم است.",
          null,
        );
      }
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      method: options.method ?? "GET",
      headers,
      credentials: "include",
      body:
        options.body === undefined ? undefined : JSON.stringify(options.body),
    });

    if (
      response.status === 401 &&
      authenticated &&
      options.retryAfterRefresh !== false
    ) {
      await this.refreshSession();
      return this.request<T>(path, { ...options, retryAfterRefresh: false });
    }

    const body = await readBody(response);
    if (!response.ok) throw toApiError(response, body);
    return body as T;
  }

  private async requestBlob(
    path: string,
    retryAfterRefresh = true,
  ): Promise<Blob> {
    const accessToken = this.session.getAccessToken();
    if (accessToken === null) {
      throw new IdentityApiError(
        401,
        "GHALISHOR_AUTH_REQUIRED",
        "ورود به حساب لازم است.",
        null,
      );
    }
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: "include",
    });
    if (response.status === 401 && retryAfterRefresh) {
      await this.refreshSession();
      return this.requestBlob(path, false);
    }
    if (!response.ok) throw toApiError(response, await readBody(response));
    return response.blob();
  }
}

export function identityErrorMessage(error: unknown): string {
  if (error instanceof IdentityApiError) return error.message;
  return "ارتباط با سرور برقرار نشد. دوباره تلاش کنید.";
}

export function isIdentityApiErrorBody(
  value: unknown,
): value is IdentityAccessApiErrorBody {
  return (
    isRecord(value) &&
    isGhalishorIdentityAccessErrorCode(value.code) &&
    typeof value.message === "string"
  );
}
